// apps/web/src/components/landing-v2/MarketingLayout.tsx
import { useRef, type ReactNode } from 'react'
import { MarketingNav } from './MarketingNav'
import { MarketingFooter } from './MarketingFooter'
import { useScrollToHash } from '../../hooks/useScrollToHash'
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll'
import { useForcedTheme } from '../../hooks/useForcedTheme'
import './landing-v2.css'

type Props = { children: ReactNode }

/** Shared chrome for landing v2 + sister pages (about, pricing, how-it-works,
 *  help). Wraps `children` between MarketingNav and MarketingFooter. We
 *  intentionally do NOT emit a `<main>` here — App.tsx already wraps the
 *  Router outlet in one, so adding another would create a duplicate main
 *  landmark (and break Playwright's strict-mode `.locator('main')` selector
 *  used by the prerender guardrail). The semantic main is owned at the
 *  app shell level.
 *
 *  Two hooks mounted here so every marketing route benefits:
 *  - useScrollToHash: React Router 7's <Link to="/#themes"> updates the
 *    URL but doesn't scroll on its own. Also scrolls plain route changes
 *    back to top.
 *  - useRevealOnScroll: every `.reveal`-classed descendant gets `.is-visible`
 *    added when it intersects the viewport. Without this, sections on
 *    sister pages (PricingCompare, FAQ items, etc.) stay opacity:0 forever.
 *    The observer rootRef points at this layout root, so it sees the whole
 *    subtree. */
export function MarketingLayout({ children }: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  useScrollToHash()
  useRevealOnScroll(rootRef)
  // Signed-out marketing surfaces render Daylight once the theme system is live
  // (flag-gated for batch 2). No-op with the flag off — zero visual change.
  useForcedTheme('light')
  return (
    <div ref={rootRef} className="min-h-screen bg-cosmic-black text-text-primary">
      <MarketingNav />
      <div>{children}</div>
      <MarketingFooter />
    </div>
  )
}
